import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getSyncConfig, saveSyncConfig,
  syncContactsBqToSheetWithOpts,
  syncContactsSheetToBqWithOpts,
  mergeContactsWithOpts,
} from '../api/backend.js';

// Default column selections (shown immediately — config API updates them in background)
const DEFAULT_BQ_TO_SHEET_COLS  = ['bd_status', 'stage_override', 'main_coverage', '_stage', '_touches', '_last_touch', 'contact_id'];
const DEFAULT_SHEET_TO_BQ_COLS  = ['relevance_score', 'notes', 'main_coverage', 'name', 'role'];
const DEFAULT_MERGE_COLS        = ['name', 'role', 'notes', 'city', 'country_territory', 'sector_coverage', 'institution_type', 'relevance_score'];

const BQ_TO_SHEET_ALL_COLS = [
  { key: 'bd_status',      label: 'BD Status' },
  { key: 'stage_override', label: 'Stage Override' },
  { key: 'main_coverage',  label: 'Main Coverage' },
  { key: '_stage',         label: 'Stage' },
  { key: '_touches',       label: 'Touches' },
  { key: '_last_touch',       label: 'Last Touch' },
  { key: '_last_email_open',  label: 'Last Email Open' },
  { key: 'contact_id',        label: 'BQ Contact ID' },
  { key: 'relevance_score',label: 'Relevance Score' },
  { key: '_firm_name',     label: 'Account Name' },
  { key: '_fund_type',     label: 'Fund Type' },
  { key: '_firm_aum',      label: 'Firm AUM' },
  { key: '_firm_owner',    label: 'Firm Owner' },
  { key: 'name',           label: 'Name' },
  { key: 'role',           label: 'BD Job Title' },
  { key: 'notes',          label: 'Notes' },
];

const SHEET_TO_BQ_ALL_COLS = [
  { key: 'relevance_score',   label: 'Relevance Score' },
  { key: 'notes',             label: 'Notes' },
  { key: 'main_coverage',     label: 'Main Coverage' },
  { key: 'name',              label: 'Name' },
  { key: 'role',              label: 'BD Job Title' },
  { key: 'bd_status',         label: 'BD Status' },
  { key: 'stage_override',    label: 'Stage Override' },
  { key: 'city',              label: 'City' },
  { key: 'country_territory', label: 'Country/Territory' },
  { key: 'sector_coverage',   label: 'Sector Coverage' },
  { key: 'industry_focus',    label: 'Industry Focus' },
  { key: 'institution_type',  label: 'Institution Type' },
];

const MERGE_ALL_COLS = [
  { key: 'name',              label: 'Name',              default: true },
  { key: 'role',              label: 'BD Job Title',      default: true },
  { key: 'notes',             label: 'Notes (concat)',    default: true },
  { key: 'city',              label: 'City',              default: true },
  { key: 'country_territory', label: 'Country/Territory', default: true },
  { key: 'sector_coverage',   label: 'Sector Coverage',   default: true },
  { key: 'institution_type',  label: 'Institution Type',  default: true },
  { key: 'relevance_score',   label: 'Relevance Score',   default: true },
  { key: 'main_coverage',     label: 'Main Coverage',     default: false },
  { key: 'bd_status',         label: 'BD Status',         default: false },
  { key: 'stage_override',    label: 'Stage Override',    default: false },
  { key: 'source',            label: 'Source',            default: false },
  { key: 'added_date',        label: 'Added Date',        default: false },
];

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function SyncPanel({ open, onClose, onDone, getToken }) {
  const closeTimerRef = useRef(null);

  // Timestamps (loaded async; null = not yet fetched)
  const [lastBqToSheet, setLastBqToSheet] = useState(null);
  const [lastSheetToBq, setLastSheetToBq] = useState(null);
  const [lastMerge, setLastMerge]         = useState(null);

  // Column selections — pre-populated with defaults immediately
  const [bqCols, setBqCols]       = useState(DEFAULT_BQ_TO_SHEET_COLS);
  const [sheetCols, setSheetCols] = useState(DEFAULT_SHEET_TO_BQ_COLS);
  const [mergeCols, setMergeCols] = useState(DEFAULT_MERGE_COLS);

  // BQ→Sheet state
  const [bqMode, setBqMode]   = useState('sync');
  const [bqRunning, setBqRunning]   = useState(false);
  const [bqResult, setBqResult]     = useState(null);
  const [showForcePushConfirm, setShowForcePushConfirm] = useState(false);

  // Sheet→BQ state
  const [stbRunning, setStbRunning] = useState(false);
  const [stbPreview, setStbPreview] = useState(null);
  const [stbResult, setStbResult]   = useState(null);

  // Merge state
  const [mergeRunning, setMergeRunning] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeResult, setMergeResult]   = useState(null);

  const [tab, setTab] = useState('bq-to-sheet');
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  // Load saved config in background — updates cols only if server has a saved preference
  const loadConfig = useCallback(async () => {
    try {
      const cfg = await getSyncConfig(getToken);
      setLastBqToSheet(cfg.last_bq_to_sheet_sync);
      setLastSheetToBq(cfg.last_sheet_to_bq_sync);
      setLastMerge(cfg.last_merge_sync);
      setBqCols(cfg.bq_to_sheet_columns);
      setSheetCols(cfg.sheet_to_bq_columns);
      setMergeCols(cfg.merge_columns);
    } catch (e) {
      console.error('loadConfig', e);
    }
  }, [getToken]);

  useEffect(() => {
    if (open) {
      loadConfig();
      setBqResult(null);
      setStbPreview(null);
      setStbResult(null);
      setMergePreview(null);
      setMergeResult(null);
      setStatusMsg('');
      setConfigDirty(false);
    }
  }, [open, loadConfig]);

  async function saveColConfig() {
    if (!configDirty) return;
    setConfigSaving(true);
    try {
      await saveSyncConfig(getToken, {
        bq_to_sheet_columns: bqCols,
        sheet_to_bq_columns: sheetCols,
        merge_columns: mergeCols,
      });
      setConfigDirty(false);
    } catch (e) {
      console.error('saveColConfig', e);
    } finally {
      setConfigSaving(false);
    }
  }

  function toggleCol(cols, setCols, key) {
    setCols((prev) => {
      setConfigDirty(true);
      return prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    });
  }

  async function runBqToSheet() {
    if (bqMode === 'force-push') { setShowForcePushConfirm(true); return; }
    await execBqToSheet();
  }

  async function execBqToSheet() {
    setShowForcePushConfirm(false);
    setBqRunning(true);
    setBqResult(null);
    setStatusMsg('Saving column preferences...');
    try {
      await saveColConfig();
      setStatusMsg('Running BQ → Sheet sync...');
      const res = await syncContactsBqToSheetWithOpts(getToken, { columns: bqCols, mode: bqMode });
      setBqResult({ ok: true, data: res });
      setLastBqToSheet(new Date().toISOString());
      setStatusMsg('BQ → Sheet completed.');
      onDone?.({ type: 'bq-to-sheet', at: Date.now() });
    } catch (e) {
      setBqResult({ ok: false, msg: e.message });
      setStatusMsg('BQ → Sheet failed.');
    } finally {
      setBqRunning(false);
    }
  }

  async function previewSheetToBq() {
    setStbRunning(true);
    setStbPreview(null);
    setStbResult(null);
    setStatusMsg('Building Sheet → BQ preview...');
    try {
      const res = await syncContactsSheetToBqWithOpts(getToken, { columns: sheetCols, dryRun: true });
      setStbPreview(res);
      setStatusMsg('Preview ready.');
    } catch (e) {
      setStbPreview({ error: e.message });
      setStatusMsg('Preview failed.');
    } finally {
      setStbRunning(false);
    }
  }

  async function execSheetToBq() {
    setStbRunning(true);
    setStbResult(null);
    setStatusMsg('Saving column preferences...');
    try {
      await saveColConfig();
      setStatusMsg('Running Sheet → BQ sync...');
      const res = await syncContactsSheetToBqWithOpts(getToken, { columns: sheetCols, dryRun: false });
      setStbResult({ ok: true, data: res });
      setLastSheetToBq(new Date().toISOString());
      setStbPreview(null);
      setStatusMsg('Sheet → BQ completed.');
      onDone?.({ type: 'sheet-to-bq', at: Date.now() });
    } catch (e) {
      setStbResult({ ok: false, msg: e.message });
      setStatusMsg('Sheet → BQ failed.');
    } finally {
      setStbRunning(false);
    }
  }

  async function previewMerge() {
    setMergeRunning(true);
    setMergePreview(null);
    setMergeResult(null);
    setStatusMsg('Building merge preview...');
    try {
      const res = await mergeContactsWithOpts(getToken, { columns: mergeCols, dryRun: true });
      setMergePreview(res);
      setStatusMsg('Preview ready.');
    } catch (e) {
      setMergePreview({ error: e.message });
      setStatusMsg('Preview failed.');
    } finally {
      setMergeRunning(false);
    }
  }

  async function execMerge() {
    setMergeRunning(true);
    setMergeResult(null);
    setStatusMsg('Saving column preferences...');
    try {
      await saveColConfig();
      setStatusMsg('Running merge...');
      const res = await mergeContactsWithOpts(getToken, { columns: mergeCols, dryRun: false });
      setMergeResult({ ok: true, data: res });
      setLastMerge(new Date().toISOString());
      setMergePreview(null);
      setStatusMsg('Merge completed.');
      onDone?.({ type: 'merge', at: Date.now() });
    } catch (e) {
      setMergeResult({ ok: false, msg: e.message });
      setStatusMsg('Merge failed.');
    } finally {
      setMergeRunning(false);
    }
  }

  async function handleClose() {
    await saveColConfig();
    onClose?.();
  }

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={{
        background: 'var(--bg-primary, #fff)', borderRadius: 12, width: 560, maxWidth: '95vw',
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 0' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Sync Contacts</div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#888', lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          {[['bq-to-sheet', 'BQ → Sheet'], ['sheet-to-bq', 'Sheet → BQ'], ['merge', '⇄ Merge']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '6px 14px', borderRadius: '6px 6px 0 0', border: 'none', cursor: 'pointer', fontSize: 13,
              fontWeight: tab === key ? 600 : 400,
              background: tab === key ? 'var(--bg-primary, #fff)' : 'transparent',
              color: tab === key ? 'var(--accent-blue, #3b82f6)' : 'var(--text-secondary, #666)',
              borderBottom: tab === key ? '2px solid var(--accent-blue, #3b82f6)' : '2px solid transparent',
            }}>{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '16px 20px 20px', flex: 1 }}>
          {(configSaving || statusMsg) && (
            <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#334155', fontSize: 12 }}>
              {configSaving ? 'Saving column preferences… ' : ''}{statusMsg}
            </div>
          )}
          {tab === 'bq-to-sheet' ? (
            <BqToSheetTab
              lastSync={lastBqToSheet}
              cols={bqCols}
              allCols={BQ_TO_SHEET_ALL_COLS}
              toggleCol={(key) => toggleCol(bqCols, setBqCols, key)}
              mode={bqMode} setMode={setBqMode}
              running={bqRunning}
              result={bqResult}
              onRun={runBqToSheet}
              showConfirm={showForcePushConfirm}
              onConfirmForce={execBqToSheet}
              onCancelForce={() => setShowForcePushConfirm(false)}
            />
          ) : tab === 'sheet-to-bq' ? (
            <SheetToBqTab
              lastSync={lastSheetToBq}
              cols={sheetCols}
              allCols={SHEET_TO_BQ_ALL_COLS}
              toggleCol={(key) => toggleCol(sheetCols, setSheetCols, key)}
              running={stbRunning}
              preview={stbPreview}
              result={stbResult}
              onPreview={previewSheetToBq}
              onExec={execSheetToBq}
              onClearPreview={() => setStbPreview(null)}
            />
          ) : (
            <MergeTab
              lastSync={lastMerge}
              cols={mergeCols}
              allCols={MERGE_ALL_COLS}
              toggleCol={(key) => toggleCol(mergeCols, setMergeCols, key)}
              running={mergeRunning}
              preview={mergePreview}
              result={mergeResult}
              onPreview={previewMerge}
              onExec={execMerge}
              onClearPreview={() => setMergePreview(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ColCheckboxes({ allCols, selected, onToggle }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8 }}>
      {allCols.map(({ key, label }) => (
        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={selected.includes(key)} onChange={() => onToggle(key)}
            style={{ accentColor: 'var(--accent-blue, #3b82f6)', width: 13, height: 13 }}/>
          {label}
        </label>
      ))}
    </div>
  );
}

function SyncResult({ result }) {
  if (!result) return null;
  if (!result.ok) return (
    <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>
      <strong>Sync failed:</strong> {result.msg || 'Unknown error'}
    </div>
  );
  const d = result.data ?? {};
  const cells = d.cells_written ?? null;
  const parts = [];
  if (d.appended > 0) parts.push(`${d.appended} rows added to Sheet`);
  if (d.inserted > 0) parts.push(`${d.inserted} rows inserted to BQ`);
  if (cells !== null && cells > 0) parts.push(`${cells} cells updated`);
  else if (cells === 0 && d.appended === 0 && (d.inserted ?? 0) === 0) parts.push('Sheet already up to date');
  else if (cells === null && (d.updated ?? 0) > 0) parts.push(`${d.updated} rows processed`);
  return (
    <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: '#166534' }}>Sync complete — {parts.join(', ') || 'nothing to do'}</div>
      {d.incremental && <div style={{ color: '#4d7c0f', marginTop: 2 }}>Incremental: {d.total_bq ?? 0} contacts checked</div>}
    </div>
  );
}

function BqToSheetTab({ lastSync, cols, allCols, toggleCol, mode, setMode, running, result, onRun, showConfirm, onConfirmForce, onCancelForce }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        Last sync: <strong style={{ color: '#333' }}>{fmtTs(lastSync)}</strong>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Columns to sync</div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Selected columns will be written from BQ into the Sheet. Preferences save when you run sync or close the panel.</div>
      <ColCheckboxes allCols={allCols} selected={cols} onToggle={toggleCol} />

      <div style={{ marginTop: 16, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Mode</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          ['sync',       'Sync',       'Smart sync — preserves manual edits in Sheet'],
          ['force-push', 'Force Push', 'Overwrites all selected columns unconditionally'],
        ].map(([val, label, desc]) => (
          <label key={val} style={{
            flex: 1, border: `1.5px solid ${mode === val ? 'var(--accent-blue,#3b82f6)' : 'var(--border,#e5e7eb)'}`,
            borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
            background: mode === val ? 'rgba(59,130,246,0.05)' : 'transparent',
          }}>
            <input type="radio" name="bq-mode" value={val} checked={mode === val} onChange={() => setMode(val)} style={{ display: 'none' }}/>
            <div style={{ fontWeight: 600, fontSize: 12, color: mode === val ? 'var(--accent-blue,#3b82f6)' : '#333' }}>{label}</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{desc}</div>
          </label>
        ))}
      </div>

      {showConfirm && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#c2410c', marginBottom: 6 }}>⚠ Confirm Force Push?</div>
          <div style={{ color: '#7c2d12', marginBottom: 10 }}>This will overwrite all selected columns, including any manually edited values in the Sheet.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onConfirmForce} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#ea580c', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Confirm overwrite</button>
            <button onClick={onCancelForce} style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}

      <button
        onClick={onRun} disabled={running || cols.length === 0}
        style={{
          marginTop: 16, width: '100%', padding: '9px 0', borderRadius: 8, border: 'none',
          background: cols.length === 0 ? '#e5e7eb' : 'var(--accent-blue,#3b82f6)',
          color: cols.length === 0 ? '#9ca3af' : '#fff', cursor: cols.length === 0 ? 'not-allowed' : 'pointer',
          fontWeight: 600, fontSize: 13,
        }}
      >{running ? 'Syncing…' : mode === 'force-push' ? 'Force Push →' : 'Sync →'}</button>

      <SyncResult result={result} />
    </div>
  );
}

function SheetToBqTab({ lastSync, cols, allCols, toggleCol, running, preview, result, onPreview, onExec, onClearPreview }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        Last sync: <strong style={{ color: '#333' }}>{fmtTs(lastSync)}</strong>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Columns to sync</div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Selected columns will be written from Sheet back to BQ (only changed rows). Preferences save when you run sync or close the panel.</div>
      <ColCheckboxes allCols={allCols} selected={cols} onToggle={toggleCol} />

      {!preview && !result && (
        <button
          onClick={onPreview} disabled={running || cols.length === 0}
          style={{
            marginTop: 16, width: '100%', padding: '9px 0', borderRadius: 8,
            background: cols.length === 0 ? '#e5e7eb' : '#f3f4f6',
            color: cols.length === 0 ? '#9ca3af' : '#374151', cursor: cols.length === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600, fontSize: 13, border: '1px solid #d1d5db',
          }}
        >{running ? 'Analyzing…' : 'Preview changes'}</button>
      )}

      {preview && !result && (
        <div style={{ marginTop: 12 }}>
          {preview.error ? (
            <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>{preview.error}</div>
          ) : (
            <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Ready to execute</div>
              <div style={{ color: '#374151', marginBottom: 2 }}>New contacts to insert: <strong>{preview.would_insert ?? 0}</strong></div>
              <div style={{ color: '#374151', marginBottom: 6 }}>Field values to update: <strong>{preview.would_update ?? 0}</strong></div>
              {preview.change_log && Object.keys(preview.change_log).length > 0 && (
                <div style={{ color: '#6b7280' }}>
                  {Object.entries(preview.change_log).map(([f, n]) => (
                    <span key={f} style={{ display: 'inline-block', marginRight: 8, marginBottom: 2 }}>
                      {f}: {n} rows
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={onExec} disabled={running} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: 'var(--accent-blue,#3b82f6)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  {running ? 'Running…' : 'Confirm & run'}
                </button>
                <button onClick={onClearPreview} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <SyncResult result={result} />
    </div>
  );
}

function MergeTab({ lastSync, cols, allCols, toggleCol, running, preview, result, onPreview, onExec, onClearPreview }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
        Last merge: <strong style={{ color: '#333' }}>{fmtTs(lastSync)}</strong>
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>
        Bidirectional fill-gaps: fills blank fields from the other side, never overwrites existing values. Notes are concatenated when both sides differ. Also aligns missing contacts in both directions.
      </div>

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Fields to merge</div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
        Enrichment fields are checked by default. All fields can be toggled freely.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 4 }}>
        {allCols.map(({ key, label, default: isDefault }) => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={cols.includes(key)} onChange={() => toggleCol(key)}
              style={{ accentColor: 'var(--accent-blue, #3b82f6)', width: 13, height: 13 }}/>
            <span style={{ color: isDefault ? '#111' : '#6b7280' }}>{label}</span>
          </label>
        ))}
      </div>

      {!preview && !result && (
        <button
          onClick={onPreview} disabled={running || cols.length === 0}
          style={{
            marginTop: 16, width: '100%', padding: '9px 0', borderRadius: 8,
            background: cols.length === 0 ? '#e5e7eb' : '#f3f4f6',
            color: cols.length === 0 ? '#9ca3af' : '#374151', cursor: cols.length === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600, fontSize: 13, border: '1px solid #d1d5db',
          }}
        >{running ? 'Analyzing…' : 'Preview merge'}</button>
      )}

      {preview && !result && (
        <div style={{ marginTop: 12 }}>
          {preview.error ? (
            <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>{preview.error}</div>
          ) : (
            <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Merge preview</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginBottom: 8 }}>
                <div>BQ fields to fill: <strong>{preview.bq_fills ?? 0}</strong></div>
                <div>Sheet fields to fill: <strong>{preview.sheet_fills ?? 0}</strong></div>
                <div>Notes to concat: <strong>{preview.notes_concat ?? 0}</strong></div>
                <div>Conflicts skipped: <strong style={{ color: '#d97706' }}>{preview.conflicts ?? 0}</strong></div>
                <div>Append to Sheet: <strong>{preview.append_to_sheet ?? 0}</strong></div>
                <div>Insert to BQ: <strong>{preview.insert_to_bq ?? 0}</strong></div>
              </div>
              {preview.conflict_details?.length > 0 && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 11, color: '#d97706', fontWeight: 600 }}>
                    View conflicts (both sides have a different value — skipped)
                  </summary>
                  <div style={{ marginTop: 6, maxHeight: 120, overflowY: 'auto' }}>
                    {preview.conflict_details.map((c, i) => (
                      <div key={i} style={{ fontSize: 10, color: '#6b7280', marginBottom: 2 }}>
                        {c.email} · {c.field}: BQ="{c.bq_value}" / Sheet="{c.sheet_value}"
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onExec} disabled={running} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: 'var(--accent-blue,#3b82f6)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  {running ? 'Running…' : 'Confirm merge'}
                </button>
                <button onClick={onClearPreview} style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        result.ok ? (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: '#166534', marginBottom: 4 }}>Merge complete</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', color: '#166534' }}>
              <div>BQ fields filled: {result.data.bq_updated ?? 0}</div>
              <div>Sheet fields filled: {result.data.sheet_updated ?? 0}</div>
              <div>Notes concatenated: {result.data.notes_concat ?? 0}</div>
              <div>Conflicts skipped: {result.data.conflicts ?? 0}</div>
              <div>Appended to Sheet: {result.data.sheet_appended ?? 0}</div>
              <div>Inserted to BQ: {result.data.bq_inserted ?? 0}</div>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>
            {result.msg}
          </div>
        )
      )}
    </div>
  );
}
